import { Recurrence, RecurrenceConverters } from '@tableau/self-service-schedules';
import { SelfServiceSchedulePane, SelfServiceSchedulePaneProps, SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { useField } from 'formik';
import React from 'react';

import { IScheduleFrequency } from '../../schedule/types/ISchedule';

export type FormikSelfServiceSchedulePaneProps =
  Omit<SelfServiceSchedulePaneProps, 'recurrenceModel' | 'recurrenceModelChangedCallback'> &
  {
    name: string;
    label?: string;
    labelId?: string;
  };

/**
 * A wrapper around {@link SelfServiceSchedulePane} which auto-wires to a {@link Formik} field using the `name` prop.
 * Other props are identical to {@link SelfServiceSchedulePane}, except there are optional props for adding a label
 * and pre-calculated converter functions along with change handler which take into account the differences
 * between interfaces {@link IScheduleFrequency} and {@link Recurrence}.
 */
export function FormikSelfServiceSchedulePane(props: FormikSelfServiceSchedulePaneProps) {
  const [field, meta, helpers] = useField<IScheduleFrequency>(props.name);
  const {name, label, labelId, ...otherProps} = props;
  const changeHandler = React.useCallback(
    (recurrence: Recurrence) => {
      const newSchedule = RecurrenceConverters.convertRecurrenceToLegacySchedule(recurrence);
      delete newSchedule.serverTimezoneOffset; // this offset is not used by IScheduleFrequency
      helpers.setValue(newSchedule);
    },
    // This deps array was noncompliant before we enabled the lint rule. If you’re here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [helpers.setValue]
  );

  return <>
    {label ? <label id={labelId}>{label}</label> : null}
    <SelfServiceSchedulePane
      externalLabelId={labelId}
      recurrenceModel={RecurrenceConverters.convertLegacyScheduleToRecurrence(field.value)}
      recurrenceModelChangedCallback={changeHandler}
      type={SelfServiceSchedulePaneType.AdminLegacySchedule}
      {...otherProps}
    />
  </>;
}
